import {
  answeredBy,
  missStatement,
  routingAnswers,
  type IRegistrationNotice,
  type IRouteAnswer,
  type ITeamRoutingQuestion
} from '@/domains/auth';
import {
  COMMON_PASSWORDS,
  HELD_IDENTIFIERS,
  MIGRATED_ACCOUNTS,
  PASSWORD_MIN_LENGTH,
  REGISTRY,
  TAKEN_ADDRESSES,
  TEAM_FIRMS,
  UNENTITLED_ACCOUNT,
  WIREHOUSE_FIRM_IDS,
  type AddressCollision,
  type IFinraFirm,
  type IHeldIdentifier,
  type IMigratedAccount,
  type ITeamFirm,
  type JourneyCode,
  type LookupResult,
  type ProviderMode,
  type Provenance,
  type SubjectKind
} from './fixtures';

/**
 * The onboarding flow as a state machine, in the server's own vocabulary.
 *
 * Two rules govern everything below, and they are what make this a specification rather
 * than a drawing:
 *
 * 1. **Where survey-api already decides something, this mirrors it exactly** — the four
 *    steps (`anchor`, `confirm_identity`, a team's `route`, `complete`) and the redirects that
 *    end a run with nothing made (a team's answers to the RIA firm survey, an RIA firm the SEC
 *    does not list to the team survey), the three lookup answers (`ok` / `not_found` /
 *    `error`), the two refusal reasons, the SEC number's one shape, the retry-then-branch rule
 *    on a second `not_found` (and the RIA door's notice at every one instead), the fact that a
 *    denied identity returns to the anchor step with the attempt count kept, and a team's route:
 *    the wirehouse list its entry is read on and SR-416's gating tree. Divergence here would be
 *    a mock of a different product.
 * 2. **Where it does not, the screen carries its own provenance** and the wire log says
 *    what would have to answer it. The activation campaign is in that half; the migrated-
 *    account first login left it with survey-api's block 3, the team gating tree with its
 *    teams block.
 *
 * Nothing in this module talks to the network. `wire[]` is a *log of the calls the real
 * flow would make*, appended on each transition — it is the deliverable for the backend as
 * much as the screens are.
 */

export type StepCode =
  // The front door: the product's own sign-in screen, and the choice that has to precede a
  // registration because the survey code cannot be derived from anything the caller types.
  | 'sign_in_entry'
  | 'choose_subject'
  // Password recovery: the request, its 202, and the redemption — all three in the product.
  | 'recover_request'
  | 'recover_sent'
  | 'recover_redeem'
  // Registration: the server's four steps, and the redirect that ends a run with nothing
  // created — a team's to the RIA firm survey, an RIA firm's to the team survey.
  | 'anchor'
  | 'confirm_identity'
  | 'route'
  | 'complete'
  | 'redirected'
  // Migrated accounts.
  | 'sign_in'
  | 'activation_notice'
  | 'set_password'
  | 'forced_change'
  | 'confirm_identifier'
  // Terminal.
  | 'landed'
  // The other landing, and the one no screen was drawn for until now: signed in, and the
  // catalogue answers `[]`.
  | 'nothing_offered'
  | 'refused'
  // A run that stopped taking steps under an open tab — cancelled from another one, or ended
  // by survey-api's seven-day sweep. Its own screen, as it is in the product.
  | 'run_closed';

/** One HTTP call the real flow would make at this transition. */
export interface IWireCall {
  method: 'GET' | 'POST';
  path: string;
  /** Sent with the session's bearer: a registration inside an account, which binds the run. */
  bearer?: boolean;
  request?: Record<string, unknown>;
  status: number;
  /** A list for the routes that answer one, as `GET /my/cycles` and `GET /my/identifiers` do. */
  response?: Record<string, unknown> | Record<string, unknown>[];
  provenance: Provenance;
  /** Why this shape, or — for a `not-built` call — what has to be built to answer it. */
  note: string;
}

export interface IDestination {
  surveyCode: string;
  /** TEAMS is the one multi-path survey: `WIRES` or `INDEPENDENT`. */
  pathCode?: string;
  subjectKind: SubjectKind;
  /** The sentence the landing screen shows: why this survey and no other. */
  why: string;
}

export interface IRefusal {
  /** What the registrant is told. Exactly the two the API answers, plus the 409. */
  reason: 'identifier_unavailable' | 'address_unavailable' | 'registration_out_of_step';
  /** What `workflow_run_event` records, which the registrant never sees. */
  staffDetail: string;
  /** Whether the run survives it — an address can be changed, an anchor cannot. */
  runContinues: boolean;
  /** True for the 54: told what a duplicate is told, and with no way out if it is the only
   *  address they have. Staff-side knowledge — nothing the registrant sees reads it. */
  deadEnd?: boolean;
}

/** What the owner answered about a number held against them — `SelfConfirmations`. */
export type SelfConfirmation = 'confirmed' | 'denied';

/** The gating tree's question codes (`TeamGatingQuestions`) — keys of the answers object on
 *  the wire, so a rename would be a contract change. */
type GatingCode = 'private_wealth' | 'trust_company' | 'ria_submission' | 'structure';

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

/**
 * survey-api's gating questions (`TeamGating.Questions`, SR-416), word for word — code, kind,
 * text and option labels. The texts are the workbook's (TEAM-RG-001's no-CRD prompt and
 * TEAM-RG-004…006), and the product renders them as they are sent, so a paraphrase here would be
 * a mock of a different form. Every one is a `choice`: the percent question is retired.
 */
const GATING: Record<GatingCode, ITeamRoutingQuestion> = {
  private_wealth: {
    code: 'private_wealth',
    kind: 'choice',
    text: 'Is your team part of a private wealth channel within your firm?',
    options: YES_NO
  },
  trust_company: {
    code: 'trust_company',
    kind: 'choice',
    text: 'Are you affiliated with a trust company?',
    options: YES_NO
  },
  ria_submission: {
    code: 'ria_submission',
    kind: 'choice',
    text: 'Are you submitting this survey on behalf of an RIA?',
    options: YES_NO
  },
  structure: {
    code: 'structure',
    kind: 'choice',
    text:
      'Are you structured as an independent practice under a Corporate RIA umbrella, ' +
      'an independent broker-dealer or Trust Company?',
    options: [
      { value: 'corporate_ria', label: 'Corporate RIA Umbrella' },
      { value: 'ibd', label: 'Independent Broker-Dealer (IBD)' },
      { value: 'ria_and_ibd', label: 'Both RIA and IBD' },
      { value: 'trust_company', label: 'Trust Company' },
      { value: 'not_independent', label: 'Not Independent' }
    ]
  }
};

/**
 * A new team's route, as survey-api keeps it on the run (`RegistrationRoute`, SR-416's B5-1 and
 * B5-2): where the tree is entered — a lead at a firm on the wirehouse list, at no firm at all,
 * or FINRA unreachable; any other firm otherwise — and the register firm that names the team
 * (exactly one register firm behind the lead's firms), with the name the view says.
 */
interface IRoute {
  wirehouse: boolean;
  noFirm: boolean;
  firm: Nullable<ITeamFirm>;
  /** `RegistrationView.firmName`: the register's label where one register firm maps the lead's
   *  firms, else FINRA's name of the first one. */
  firmName: Nullable<string>;
}

/** What a team's route came to, as the catalogue's `teamRouting` reads it after the
 *  completion. An outcome of `ria` is not one: it ends the run (`redirected`). A registration's
 *  routing is always its answers' since SR-416: every entry of the tree asks a question first,
 *  and no firm routes a team alone. */
export interface ITeamRouting {
  outcome: 'wires' | 'independent';
  pathCode: 'WIRES' | 'INDEPENDENT';
  source: 'answers';
  firm: Nullable<ITeamFirm>;
  answers: Record<string, string>;
}

/** One row of what the account holds, as the landing and the identifier card read it. */
export interface IStoredIdentifier {
  idType: string;
  value: string;
  verified: boolean;
  selfConfirmation: Nullable<SelfConfirmation>;
  /** Whose number it is — the participant's display name, which the product's card joins in
   *  from `GET /my/participants`. Null where the run composed none. */
  holder: Nullable<string>;
}

export interface IRunState {
  journey: JourneyCode;
  providerMode: ProviderMode;
  runId: string;
  step: StepCode;

  /**
   * Whether the run is bound to a signed-in account — `RegistrationView.signedIn`: made from
   * "Add a survey", its completion takes no address or password and adds to that account
   * (`/portal/surveys/add`, the teams plan's R7).
   */
  signedIn: boolean;

  anchorIdType: string;
  anchorValue: Nullable<string>;
  lookup: Nullable<LookupResult>;
  /** The participant we already hold, on the linking branch alone — `RegistrationView`'s own
   *  member, and the one branch where naming somebody from our records is the point. */
  displayName: Nullable<string>;
  /** Who the registry says holds the number, on an `ok` that names one. Beside `displayName`
   *  rather than instead of it: on the linking branch both arrive, and they need not agree. */
  registryName: Nullable<string>;
  /** The firm BrokerCheck names, for the prompt's card — which the confirmation's view does
   *  **not** carry, so the card tags it as not built. A team's route reads the firms below. */
  firmName: Nullable<string>;
  /** Every firm BrokerCheck lists the number's holder at now — what a team's route is read
   *  from, on the register, once the lead is confirmed. */
  leadFirms: IFinraFirm[];
  /** The paired NPN / licence a single NIPR lookup returns (FSP-AC-001). Nothing sends it. */
  pairedIdentifier: Nullable<{ idType: string; value: string }>;
  /** Set when the number already names a participant nothing can log in as. */
  linkTo: Nullable<IHeldIdentifier>;
  attempts: number;

  /** A team's route, from the step that reads it on (`firmName` on the view). */
  route: Nullable<IRoute>;
  /** The route step's current question, as survey-api serves it. */
  question: Nullable<ITeamRoutingQuestion>;
  /** The answers given so far, in order — the page's in the product, since survey-api keeps
   *  none between calls, and every request carries all of them. */
  given: IRouteAnswer[];
  /** The answer "Change my last answer" took back, which its question starts from. */
  takenBack: Nullable<IRouteAnswer>;
  /** survey-api's refusal of the answer just sent, said on its question. */
  answerRefused: Nullable<string[]>;
  /** What the route came to, where it reached an outcome the team keeps. */
  routing: Nullable<ITeamRouting>;

  redirected: boolean;
  destination: IDestination;
  refusal: Nullable<IRefusal>;
  /** Inline message on the current screen — a wrong password, a denied identity. */
  message: Nullable<string>;
  passwordFailures: string[];
  /** Why the anchor refused what was typed, drawn under the number as the product draws it:
   *  an SEC number in any other shape, the one refusal that is about a value's form. */
  anchorFailure: Nullable<string>;

  email: Nullable<string>;
  /** What the account ends up holding: whether something other than its owner corroborated
   *  it, and — a different question — what the owner said about it. */
  storedIdentifier: Nullable<IStoredIdentifier>;
  /** What a completion inside an account added — the product's `IAddedSurveyRouteState`,
   *  said once on the catalogue it lands on. */
  added: Nullable<{ survey: string; destinationSurvey: string; redirected: boolean }>;

  wire: IWireCall[];
}

export type FlowAction =
  | { type: 'restart' }
  | { type: 'create-account' }
  | { type: 'back' }
  | { type: 'back-to-chooser' }
  | { type: 'recover' }
  | { type: 'request-reset'; email: string }
  | { type: 'open-redeem' }
  | { type: 'redeem-reset'; token: string; password: string }
  | {
      type: 'journey';
      journey: JourneyCode;
      mode: ProviderMode;
      atStep?: StepCode;
      /** A run made inside an account, as the one before it was — `?from=account`. */
      signedIn: boolean;
    }
  | { type: 'add-survey' }
  | { type: 'anchor'; value: string; idType: string }
  | { type: 'no-anchor' }
  | { type: 'identity'; confirmed: boolean }
  | { type: 'route'; answer: string }
  | { type: 'redirect' }
  | { type: 'take-back' }
  | { type: 'complete'; email: string; password: string; fields: Record<string, string> }
  | { type: 'stale-tab' }
  | { type: 'swept' }
  | { type: 'sign-in'; email: string; password: string }
  | { type: 'redeem-token' }
  | { type: 'set-password'; password: string; currentPassword?: string }
  | { type: 'confirm-identifier'; confirmed: boolean }
  | { type: 'no-subject' };

/* ===========================================================================
   Per-survey registration data — `RegistrationSurveys.All`, mirrored
   ======================================================================== */

export interface IRegistrationField {
  name: string;
  /** The usage the first response proposes it into — none for a team's name, which no Teams
   *  usage asks. */
  usageCode: Nullable<string>;
  label: string;
  partOfDisplayName: boolean;
}

interface IRegistrationSurvey {
  surveyCode: string;
  subjectKind: SubjectKind;
  anchorIdTypes: string[];
  anchorLabel: string;
  registry: string;
  factKey: string;
  /**
   * Whether survey-api's provider for this registry is still `NotImplementedFactProvider` —
   * what the switch's `stubbed` position reproduces, so that position stays the product as it
   * stands. BrokerCheck is real since block 3 and SEC ADV since block 4; NIPR waits on block 9.
   */
  providerStubbed: boolean;
  fields: IRegistrationField[];
  /** Where an unmatched number may send the registrant, by their choice (`/redirection`): RIA's
   *  firm to the team survey. Set, the anchor answers the notice at every `not_found`. */
  noMatch?: { toSurvey: string; workflowCode: string };
}

/**
 * Who answers a match, in the product's words where survey-api's SR-396 asks the SEC's IAPD
 * beside FINRA's BrokerCheck — a person's CRD, an advisor's or a team's lead's, whose name or
 * match can be either registry's (`answeredBy`, the owner's Q2; FA's and TEAMS' words are one) —
 * and the study's own name for every other registry. An `error` stays `registry`'s: the pair's
 * is BrokerCheck's.
 */
export function answerer(survey: IRegistrationSurvey): string {
  return survey.factKey === 'brokercheck.person' ? answeredBy('FA') : survey.registry;
}

export const REGISTRATION: Record<JourneyCode, IRegistrationSurvey> = {
  // The front door belongs to no survey: choosing one is what it is for. The placeholder
  // code is what the rail shows as the destination, and "UNDECIDED" is the true answer
  // until the chooser is answered.
  entry: {
    surveyCode: 'UNDECIDED',
    subjectKind: 'person',
    anchorIdTypes: ['CRD'],
    anchorLabel: 'CRD number',
    registry: 'BrokerCheck (FINRA)',
    factKey: 'brokercheck.person',
    providerStubbed: false,
    fields: []
  },
  fa: {
    surveyCode: 'FA',
    subjectKind: 'person',
    anchorIdTypes: ['CRD'],
    anchorLabel: 'CRD number',
    registry: 'BrokerCheck (FINRA)',
    factKey: 'brokercheck.person',
    providerStubbed: false,
    fields: [
      { name: 'legalFirstName', usageCode: 'FA-AP-001', label: 'Legal first name', partOfDisplayName: true },
      { name: 'legalLastName', usageCode: 'FA-AP-002', label: 'Legal last name', partOfDisplayName: true }
    ]
  },
  fsp: {
    surveyCode: 'FSP',
    subjectKind: 'person',
    anchorIdTypes: ['NPN', 'INSURANCE_LICENSE'],
    anchorLabel: 'NAIC NPN number',
    registry: 'NIPR',
    factKey: 'nipr.person',
    providerStubbed: true,
    fields: [
      { name: 'legalFirstName', usageCode: 'FSP-AP-001', label: 'Legal first name', partOfDisplayName: true },
      { name: 'legalLastName', usageCode: 'FSP-AP-002', label: 'Legal last name', partOfDisplayName: true }
    ]
  },
  ria: {
    surveyCode: 'RIA_FIRM',
    subjectKind: 'org',
    anchorIdTypes: ['SEC_NUMBER'],
    anchorLabel: 'Firm SEC number',
    registry: 'SEC ADV',
    factKey: 'adv.firm_profile',
    providerStubbed: false,
    fields: [
      { name: 'firmName', usageCode: 'RIA-FP-001', label: 'Official firm name', partOfDisplayName: true }
    ],
    noMatch: { toSurvey: 'TEAMS', workflowCode: 'redirect_to_teams' }
  },
  // The lead advisor's CRD, asked of FINRA as a person's, and held by the team — an org
  // participant — as its `TEAM_LEAD_CRD` (survey-api's teams block). The team's name lands in
  // TEAM-TP-001, and its routing answers in TEAM-RG-004…006, locked (`RoutingUsages`).
  teams: {
    surveyCode: 'TEAMS',
    subjectKind: 'org',
    anchorIdTypes: ['CRD'],
    anchorLabel: 'Lead advisor’s CRD number',
    registry: 'BrokerCheck (FINRA)',
    factKey: 'brokercheck.person',
    providerStubbed: false,
    fields: [{ name: 'teamName', usageCode: 'TEAM-TP-001', label: 'Team name', partOfDisplayName: true }]
  },
  returning: {
    surveyCode: 'FA',
    subjectKind: 'person',
    anchorIdTypes: ['CRD'],
    anchorLabel: 'CRD number',
    registry: 'BrokerCheck (FINRA)',
    factKey: 'brokercheck.person',
    providerStubbed: false,
    fields: []
  },
  activation: {
    surveyCode: 'FA',
    subjectKind: 'person',
    anchorIdTypes: ['CRD'],
    anchorLabel: 'CRD number',
    registry: 'BrokerCheck (FINRA)',
    factKey: 'brokercheck.person',
    providerStubbed: false,
    fields: []
  },
  // No survey, which is the whole journey: the catalogue answers `[]` and the code below is
  // what the rail shows in its place. Nothing here asks for an anchor — the screen at the
  // end offers the four registrations to choose from, and each of those carries its own.
  unentitled: {
    surveyCode: 'NONE',
    subjectKind: 'person',
    anchorIdTypes: ['CRD'],
    anchorLabel: 'CRD number',
    registry: 'BrokerCheck (FINRA)',
    factKey: 'brokercheck.person',
    providerStubbed: false,
    fields: []
  }
};

const SURVEY_TITLE: Record<string, string> = {
  FA: 'Top Financial Advisors',
  FSP: 'Top Financial Security Professionals',
  RIA_FIRM: 'Top RIA Firms',
  TEAMS: 'Top Wealth Advisor Teams'
};

/** The Teams definition's two paths, by the names survey-api's seed gives them. */
const PATH_NAME: Record<string, string> = {
  WIRES: 'Wires Team Survey',
  INDEPENDENT: 'Independent Practices Survey'
};

export function surveyTitle(surveyCode: string): string {
  return SURVEY_TITLE[surveyCode] ?? surveyCode;
}

/** A survey, and the path where it has one — the start card's label, `startLabel`. */
export function destinationTitle(destination: IDestination): string {
  const title = surveyTitle(destination.surveyCode);
  const path = destination.pathCode && PATH_NAME[destination.pathCode];
  return path ? `${title} · ${path}` : title;
}

/* ===========================================================================
   The machine
   ======================================================================== */

/** A run id looks like one the server would hand back; nothing depends on its value. */
function newRunId(): string {
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${hex()}${hex()}-${hex()}-4${hex().slice(1)}-a${hex().slice(1)}-${hex()}${hex()}${hex()}`;
}

/**
 * Where each journey opens.
 *
 * A table rather than a chain of conditions, and one entry per journey rather than a
 * default: a journey added to `JourneyCode` without a first screen is then a compile error
 * instead of a silent landing on the anchor, which is the wrong screen for every journey
 * that does not start by asking for a number.
 */
const FIRST_STEP: Record<JourneyCode, StepCode> = {
  entry: 'sign_in_entry',
  fa: 'anchor',
  fsp: 'anchor',
  ria: 'anchor',
  teams: 'anchor',
  returning: 'sign_in',
  activation: 'activation_notice',
  unentitled: 'sign_in'
};

/** The session a run is made under — the account "Add a survey" adds to — or none. */
export type RunSession = Nullable<{ email: string }>;

/** The session a run was made under, for the run that follows it. */
function sessionOf(state: IRunState): RunSession {
  return state.signedIn ? { email: state.email ?? UNENTITLED_ACCOUNT.email } : null;
}

/** The four journeys that open a registration run the moment they start. */
const REGISTERING: JourneyCode[] = ['fa', 'fsp', 'ria', 'teams'];

/**
 * @param atStep Where to open, when it is not the journey's own first screen. One caller:
 *   the step back out of a registration's first screen, which belongs on the chooser rather
 *   than on the sign-in screen the door itself starts at.
 * @param session The account a run is made inside ("Add a survey"), or null for an anonymous
 *   one. Signed in, the door opens on the chooser: there is nobody to sign in.
 */
export function startRun(
  journeyCode: JourneyCode,
  providerMode: ProviderMode,
  atStep?: StepCode,
  session: RunSession = null
): IRunState {
  const survey = REGISTRATION[journeyCode];
  const signedIn = session !== null;

  const state: IRunState = {
    journey: journeyCode,
    providerMode,
    runId: newRunId(),
    step:
      atStep ?? (signedIn && journeyCode === 'entry' ? 'choose_subject' : FIRST_STEP[journeyCode]),
    signedIn,
    anchorIdType: survey.anchorIdTypes[0],
    anchorValue: null,
    lookup: null,
    displayName: null,
    registryName: null,
    firmName: null,
    leadFirms: [],
    pairedIdentifier: null,
    linkTo: null,
    attempts: 0,
    route: null,
    question: null,
    given: [],
    takenBack: null,
    answerRefused: null,
    routing: null,
    redirected: false,
    destination: initialDestination(survey),
    refusal: null,
    message: null,
    passwordFailures: [],
    anchorFailure: null,
    email: session?.email ?? null,
    storedIdentifier: null,
    added: null,
    wire: []
  };

  if (REGISTERING.includes(journeyCode)) {
    return {
      ...state,
      wire: [
        {
          method: 'POST',
          path: '/auth/registrations',
          bearer: signedIn,
          request: { survey: survey.surveyCode },
          status: 201,
          response: registrationView(state),
          provenance: 'shipped',
          note: signedIn
            ? 'Sent with the session’s bearer, so the run is bound to that account: every ' +
              'route of it answers an empty 404 to anybody else, and the completion adds to ' +
              'the account rather than making one. Any session binds, staff’s included — ' +
              'which is why the product offers “Add a survey” to advisors alone.'
            : 'The run is the state: there is no session, and no account or participant ' +
              'exists until the last step. No open cycle is asked for — registration opens ' +
              'no response, so it is open whether or not the survey is.'
        }
      ]
    };
  }

  return state;
}

/** Where a run lands until an answer moves it: the survey it was opened for. */
function initialDestination(survey: IRegistrationSurvey): IDestination {
  return {
    surveyCode: survey.surveyCode,
    subjectKind: survey.subjectKind,
    why: 'Where this registration started.'
  };
}

/**
 * The `RegistrationView` a step answers, every member as survey-api spells it — so the log
 * shows `displayName` and `registryName` side by side, and a reader never has to guess whether
 * a member was null or left out. A team's `firmName` rides on every view from its route step
 * on; `question` on the route step alone; `notice` on the RIA door's `not_found` anchor alone.
 * `sightings` is always `[]`: the study's records hold no CRD seen on another survey.
 */
function registrationView(state: IRunState): Record<string, unknown> {
  const survey = REGISTRATION[state.journey];
  return {
    runId: state.runId,
    survey: survey.surveyCode,
    destinationSurvey: state.destination.surveyCode,
    step: state.step,
    idTypes: survey.anchorIdTypes,
    lookup: state.lookup,
    displayName: state.displayName,
    registryName: state.registryName,
    fields: survey.fields.map((field) => field.name),
    attempts: state.attempts,
    redirected: state.redirected,
    firmName: state.route?.firmName ?? null,
    question: state.step === 'route' ? state.question : null,
    sightings: [],
    notice: riaDoorNotice(state),
    signedIn: state.signedIn
  };
}

/** `RegistrationSurveys`' `NotFoundNotice` for RIA_FIRM, word for word — the one notice
 *  survey-api sends, which the product draws as sent. */
const RIA_DOOR_NOTICE = {
  code: 'sec_not_registered',
  text: 'Only SEC registered firms are eligible for ranking.'
} as const;

/** The notice the anchor answers at every `not_found` of a survey with a way out — RIA_FIRM's
 *  alone. */
export function riaDoorNotice(state: IRunState): Nullable<IRegistrationNotice> {
  const door = state.step === 'anchor' && state.lookup === 'not_found';
  return door && REGISTRATION[state.journey].noMatch ? RIA_DOOR_NOTICE : null;
}

function lookupRegistry(
  survey: IRegistrationSurvey,
  idType: string,
  value: string,
  mode: ProviderMode
) {
  // The stubbed position is the product as it stands, so only a registry survey-api still
  // stubs answers `error` there — BrokerCheck answers in it as it does in the live one.
  if (mode === 'stubbed' && survey.providerStubbed) {
    return { result: 'error' as LookupResult, row: undefined };
  }
  const row = REGISTRY.find((entry) => entry.idType === idType && entry.value === value.trim());
  return { result: (row ? 'ok' : 'not_found') as LookupResult, row };
}

/** What a number is held as: a team keeps its lead's CRD as `TEAM_LEAD_CRD`, and is refused or
 *  linked on that type alone — a person's own CRD of the same value is another number. */
function heldType(journey: JourneyCode, idType: string): string {
  return journey === 'teams' && idType === 'CRD' ? 'TEAM_LEAD_CRD' : idType;
}

function heldIdentifier(idType: string, value: string): IHeldIdentifier | undefined {
  return HELD_IDENTIFIERS.find(
    (entry) => entry.idType === idType && entry.value === value.trim()
  );
}

/**
 * `PasswordPolicy.Validate`, mirrored — length, the blocklist, and the address's local part.
 *
 * Module-private on purpose. It is the study's one deliberate second evaluator (CLAUDE.md's
 * rules of the code), and keeping it here means no screen can import it and start judging a
 * password the real completion form leaves to the server's `ValidationProblem`.
 */
function passwordFailures(password: string, email: string): string[] {
  const failures: string[] = [];
  if (password.length < PASSWORD_MIN_LENGTH) {
    failures.push(`Must be at least ${PASSWORD_MIN_LENGTH} characters long.`);
  }
  if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
    failures.push('Is among the most commonly used passwords.');
  }
  const localPart = email.split('@')[0];
  if (localPart.length >= 4 && password.toLowerCase().includes(localPart.toLowerCase())) {
    failures.push('Must not contain the account’s email name.');
  }
  return failures;
}

/** survey-api's refusal of an SEC number in any other shape, word for word — the product
 *  renders the server's copy, and this is what it sends. */
const SEC_NUMBER_REFUSAL = 'Must be an SEC number such as 801-12345.';

/**
 * `SecNumbers.TryCanonicalize`, mirrored — survey-api's one spelling of an SEC number (its SEC
 * ADV plan, R8): `801-N` or `802-N` with no leading zeros and at most six digits. The prefix
 * may come with or without its dash and whitespace around it is ignored, but a run of digits
 * with no separator must be seven long at least — six or fewer is a firm CRD in the wrong box.
 * Null for anything else, which the anchor refuses where it was typed rather than looking up.
 *
 * Module-private for `passwordFailures`' reason: it is the study's second deliberate evaluator,
 * and the real anchor step leaves the shape to the server's `ValidationProblem`.
 */
function canonicalSecNumber(value: string): Nullable<string> {
  const match = /^\s*(80[12])(\s*-\s*|\s+)?([0-9]+)\s*$/.exec(value);
  if (!match) return null;
  const [, prefix, separator, digits] = match;
  if (separator === undefined && prefix.length + digits.length < 7) return null;
  const significant = digits.replace(/^0+/, '');
  if (significant.length === 0 || significant.length > 6) return null;
  return `${prefix}-${significant}`;
}

/* --- a team's route: survey-api's `RegistrationRouting` and `TeamGating` ---------- */

/**
 * The route a confirmed lead gives a team (`RegistrationFlowService.RouteConfirmedAsync`): whether
 * any of the lead's current firms — FINRA BrokerCheck's, or since SR-396 the SEC's IAPD's: the
 * study's records are FINRA's alone — is on the wirehouse list, whether the lead is at no firm, the
 * register firm when exactly one maps those firms, and the view's `firmName`.
 */
function routeOf(firms: readonly IFinraFirm[]): IRoute {
  const ids = new Set(firms.map((firm) => firm.finraFirmId));
  const register = TEAM_FIRMS.filter((firm) => firm.finraFirmIds.some((id) => ids.has(id)));
  const one = register.length === 1 ? register[0] : null;
  return {
    wirehouse: firms.some((firm) => WIREHOUSE_FIRM_IDS.includes(firm.finraFirmId)),
    noFirm: firms.length === 0,
    firm: one,
    firmName: one?.label ?? firms[0]?.firmName ?? null
  };
}

/** No firm at all — no CRD, or one FINRA does not know: the trust company question (TEAM-RG-001). */
const NO_FIRM: IRoute = {
  wirehouse: false,
  noFirm: true,
  firm: null,
  firmName: null
};

/** FINRA could not be asked: the list cannot be read, so the team is asked as one off it —
 *  survey-api's reading (B5-2), where a wirehouse team reaches Wires through "Not Independent". */
const UNASKED: IRoute = { ...NO_FIRM, noFirm: false };

type RouteOutcome = 'wires' | 'independent' | 'ria';

type RouteResult =
  | { kind: 'ask'; question: ITeamRoutingQuestion }
  | { kind: 'routed'; outcome: RouteOutcome; firm: Nullable<ITeamFirm> }
  | { kind: 'refused'; code: string; reason: 'not_asked' | 'invalid_answer' };

type Answers = Record<string, string>;

/** `TeamGatingRefusals.Describe`: the predicate survey-api says a refused answer's key with. */
function describeRefusal(reason: 'not_asked' | 'invalid_answer'): string {
  return reason === 'not_asked'
    ? 'is not asked on the way these answers take'
    : 'is not an answer this question takes';
}

/**
 * `RegistrationRouting.Evaluate`, mirrored: the tree entered by the lead's firm — the wirehouse
 * list at `private_wealth`, no firm at `trust_company`, any other firm or an outage at
 * `ria_submission` — and walked by `TeamGating`. No firm routes a team alone and nothing asks
 * which firm. Stateless like survey-api's — every answer, each time — and module-private for
 * `passwordFailures`' reason: the study's third deliberate evaluator, where the product renders
 * whatever question the server sends and decides nothing.
 */
function evaluateRoute(route: IRoute, answers: Answers): RouteResult {
  const result = walkGating(entryOf(route), answers);
  return result.kind === 'routed' ? { ...result, firm: route.firm } : result;
}

/** Where the tree is entered (`TeamGatingEntry`): an outage reads as a firm off the list. */
function entryOf(route: IRoute): GatingCode {
  if (route.wirehouse) return 'private_wealth';
  if (route.noFirm) return 'trust_company';
  return 'ria_submission';
}

/**
 * `TeamGating.Evaluate`: the tree walked from its entry, stopping at the first question with
 * no answer — and an answer left over after the walk is to a question these answers never
 * reach, refused rather than dropped.
 */
function walkGating(entry: GatingCode, answers: Answers): RouteResult {
  const asked = new Set<string>();
  const result = ((): RouteResult => {
    let code = entry;
    for (;;) {
      asked.add(code);
      const question = GATING[code];
      if (!(code in answers)) return { kind: 'ask', question };
      const value = answers[code];
      if (!question.options.some((option) => option.value === value)) {
        return { kind: 'refused', code, reason: 'invalid_answer' };
      }
      const next = gatingBranch(code, value);
      if (typeof next !== 'string') return { kind: 'routed', outcome: next.outcome, firm: null };
      code = next;
    }
  })();
  if (result.kind === 'refused') return result;
  const notAsked = Object.keys(answers).find((code) => !asked.has(code));
  return notAsked === undefined ? result : { kind: 'refused', code: notAsked, reason: 'not_asked' };
}

/** SR-416's B5-2 table, one branch per row — `TeamGating.Walk`'s switch. */
function gatingBranch(code: GatingCode, value: string): GatingCode | { outcome: RouteOutcome } {
  switch (code) {
    // Either answer goes on to the wirehouse survey; the answer is kept for the response.
    case 'private_wealth':
      return { outcome: 'wires' };
    case 'trust_company':
      return value === 'yes' ? { outcome: 'independent' } : 'ria_submission';
    case 'ria_submission':
      return value === 'yes' ? { outcome: 'ria' } : 'structure';
    case 'structure':
      return { outcome: value === 'not_independent' ? 'wires' : 'independent' };
  }
}

/** Where a routed team lands — always by its answers (see `ITeamRouting`). */
function teamRouting(
  outcome: 'wires' | 'independent',
  firm: Nullable<ITeamFirm>,
  answers: Answers
): ITeamRouting {
  return {
    outcome,
    pathCode: outcome === 'wires' ? 'WIRES' : 'INDEPENDENT',
    source: 'answers',
    firm,
    answers
  };
}

function teamDestination(routing: ITeamRouting): IDestination {
  const firm = routing.firm?.label;
  return {
    surveyCode: 'TEAMS',
    pathCode: routing.pathCode,
    subjectKind: 'org',
    why:
      `The team’s answers route it to the ${PATH_NAME[routing.pathCode]}` +
      `${firm ? `, at ${firm}` : ''}. survey-api fills the path when the survey is started, and ` +
      'proposes the answers into TEAM-RG-004…006, locked.'
  };
}

function append(state: IRunState, ...calls: IWireCall[]): IWireCall[] {
  return [...state.wire, ...calls];
}

/** survey-api's `ProblemTypes` for the two registration refusals, as the wire spells them —
 *  here rather than imported, because the study imports no API module. */
const OUT_OF_STEP = 'urn:veritant-surveys:problem:registration-out-of-step';
const OUT_OF_STEP_TITLE = 'The registration is not on that step.';
const REFUSED = 'urn:veritant-surveys:problem:registration-refused';
const REFUSED_TITLE = 'This registration cannot continue with that value.';

/**
 * `RegistrationState.BackToAnchor`, mirrored: the entry step with everything the last number
 * established dropped — the type and value it was read as, what the registry answered and the
 * name it gave, the participant it named, the redirect it earned — and the attempts kept, since
 * the retry-then-branch rule reads them. One function for survey-api's doors to it, "that is
 * not me" and the revision, so they cannot come to clear different things. A team's route and
 * its answers go as well: the firm they were read on came with the number.
 */
function backToAnchor(state: IRunState): IRunState {
  const survey = REGISTRATION[state.journey];
  return {
    ...state,
    step: 'anchor',
    anchorIdType: survey.anchorIdTypes[0],
    anchorValue: null,
    lookup: null,
    displayName: null,
    registryName: null,
    firmName: null,
    leadFirms: [],
    pairedIdentifier: null,
    linkTo: null,
    route: null,
    question: null,
    given: [],
    takenBack: null,
    answerRefused: null,
    routing: null,
    redirected: false,
    destination: initialDestination(survey),
    refusal: null,
    message: null,
    passwordFailures: [],
    anchorFailure: null
  };
}

/**
 * The one `message` that is good news rather than a refusal.
 *
 * `message` carries a wrong password, a denied identity and this, and the sign-in screen
 * colours the last one differently — so the screen has to be able to recognise it. A shared
 * constant rather than the screen sniffing the wording: a reworded sentence would otherwise
 * turn "your password is set" red without anything looking broken.
 */
export const PASSWORD_SET_MESSAGE = 'Your password is set. Sign in with it.';

/**
 * The end of the empty catalogue: the four registrations were offered and none of them is
 * this person. A constant for the same reason as the one above — the screen draws it as a
 * conclusion rather than as a refusal, and a screen sniffing the wording breaks the first
 * time somebody rewords it.
 *
 * It promises nothing about later, deliberately. Entitlement keys on the **survey**, not on
 * the cycle, so a new cycle of a ranking this participant is not entitled to leaves the page
 * exactly as empty — "check back when the next cycle opens" would be the same untrue
 * sentence the product's own dashboard used to carry.
 */
export const NO_SUBJECT_MESSAGE =
  'Then there is nothing here for you to fill in. Nothing on this page changes until our ' +
  'records connect you to a ranking.';

export function reduce(state: IRunState, action: FlowAction): IRunState {
  switch (action.type) {
    case 'restart':
      return startRun(state.journey, state.providerMode, undefined, sessionOf(state));

    // The link under the sign-in form. No run is opened here: the survey code is what
    // `POST /auth/registrations` takes, and the next screen is where it comes from.
    case 'create-account':
      return {
        ...state,
        step: 'choose_subject',
        message: null,
        wire: append(state, {
          method: 'GET',
          path: '/registration (a client route, not an API call)',
          status: 200,
          provenance: 'shipped',
          note:
            'The product’s `/login` carries this link, so it is no longer sign-in and ' +
            'recovery alone: `/registration` opens on the same question, and no run exists ' +
            'until it is answered.'
        })
      };

    // The URL owns which journey and which provider mode, so switching either is a fresh
    // run rather than a mutation of this one: a run carries the survey it was started for
    // in its own state, and the server would have to be asked for a new one too.
    // A run inside an account follows one: it keeps the address that account signs in with.
    case 'journey':
      return startRun(
        action.journey,
        action.mode,
        action.atStep,
        action.signedIn ? { email: state.email ?? UNENTITLED_ACCOUNT.email } : null
      );

    // "Add a survey", from the catalogue: the chooser again, inside the account.
    case 'add-survey': {
      const next = startRun('entry', state.providerMode, 'choose_subject', {
        email: state.email ?? UNENTITLED_ACCOUNT.email
      });
      return {
        ...next,
        wire: [
          {
            method: 'GET',
            path: '/portal/surveys/add (a client route, not an API call)',
            status: 200,
            provenance: 'shipped',
            note:
              'Registration, inside the account: the same four choices and steps as ' +
              '/registration, each request carrying the session’s bearer. Drawn for an ' +
              'advisor alone, since any session would bind the run — a staff one included.'
          }
        ]
      };
    }

    case 'anchor':
      return submitAnchor(state, action.value.trim(), action.idType);

    case 'no-anchor':
      return declareNoAnchor(state);

    case 'identity':
      return decideIdentity(state, action.confirmed);

    case 'route':
      return answerRoute(state, [
        ...state.given,
        { question: state.question as ITeamRoutingQuestion, value: action.answer }
      ]);

    case 'redirect':
      return redirectRun(state);

    // "Change my last answer": the set without its last, which survey-api answers with the
    // question that answer was to — and that question starts from the answer taken back.
    case 'take-back': {
      const last = state.given[state.given.length - 1];
      if (!last) return state;
      return { ...answerRoute(state, state.given.slice(0, -1)), takenBack: last };
    }

    case 'complete':
      return complete(state, action.email.trim(), action.password, action.fields);

    case 'back':
      return goBack(state);

    // The step back out of a registration's first screen, onto the chooser — which is the
    // door's journey, so the page moves the URL along with it. The run the choice opened ends
    // here rather than sitting in the staff queue as somebody who walked away at the anchor.
    case 'back-to-chooser': {
      const chooser = startRun('entry', state.providerMode, 'choose_subject', sessionOf(state));
      return {
        ...chooser,
        wire: append(state, {
          method: 'POST',
          path: `/auth/registrations/${state.runId}/abandonment`,
          bearer: state.signedIn,
          status: 204,
          provenance: 'shipped',
          note:
            'No body — a reason field would be free text an anonymous caller writes into a ' +
            'run staff read — and 204 with nothing to render. The run ends as abandoned, so ' +
            'the staff queue says what happened instead of showing somebody stuck at the ' +
            'anchor.'
        })
      };
    }

    case 'recover':
      return { ...state, step: 'recover_request', message: null, passwordFailures: [] };

    case 'request-reset':
      return {
        ...state,
        email: action.email.trim(),
        step: 'recover_sent',
        wire: append(state, {
          method: 'POST',
          path: '/auth/password/reset-requests',
          request: { email: action.email.trim() },
          status: 202,
          provenance: 'shipped',
          note:
            '202 for every address alike, known or not — anything else turns the form into a ' +
            'test for whether somebody has an account here, and survey-api mails from its ' +
            'worker so that not even the response time tells. Everywhere but production the ' +
            'request also takes deliverTo, the test inbox the mail goes to instead — drawn by ' +
            'the product only where GET /auth/mail-delivery says redirect, so not here.'
        })
      };

    case 'open-redeem':
      return { ...state, step: 'recover_redeem', message: null, passwordFailures: [] };

    case 'redeem-reset': {
      const failures = passwordFailures(action.password, state.email ?? '');
      if (failures.length > 0) return { ...state, passwordFailures: failures };
      return {
        ...state,
        step: 'sign_in_entry',
        passwordFailures: [],
        message: PASSWORD_SET_MESSAGE,
        wire: append(state, {
          method: 'POST',
          path: '/auth/password/reset',
          request: { token: action.token || '…', newPassword: '••••••••' },
          status: 204,
          provenance: 'shipped',
          note:
            'Shipped, and it issues **no session** — which is why it ends on the sign-in ' +
            'screen rather than inside the app. The token rides in the URL fragment and is ' +
            'never put in the query: nginx logs in the default combined format, and a query ' +
            'parameter would write a live single-use credential into the server log.'
        })
      };
    }

    // A tab left on a team's route has lost its answers with the page: the step is re-read and
    // asks its first question again (the teams plan's R3).
    case 'stale-tab':
      return {
        ...state,
        ...(state.step === 'route' && state.route ? restartRoute(state.route) : {}),
        refusal: {
          reason: 'registration_out_of_step',
          staffDetail: `The run is on ${state.step}; the request named a different step.`,
          runContinues: true
        },
        wire: append(state, {
          method: 'POST',
          path: `/auth/registrations/${state.runId}/anchor`,
          status: 409,
          response: {
            type: OUT_OF_STEP,
            title: OUT_OF_STEP_TITLE,
            step: state.step,
            status: 'in_progress'
          },
          provenance: 'shipped',
          note:
            'Two members rather than one: `step` is always one of the flow’s own steps so a ' +
            'client can switch on it and re-render, and `status` says whether the run takes ' +
            'any step at all. Neither is a disclosure — the caller already holds the run. ' +
            'The screen above re-renders the step the run is actually on and says so, which ' +
            'is the whole point of the two members travelling back. On a team’s route step ' +
            'the answers do not come back: survey-api keeps none, so its questions start ' +
            'again from the first.'
        })
      };

    // A tab left open for a week and then used. survey-api's sweep ended the run as abandoned
    // in the meantime, and the read a resumed tab makes answers 409 rather than a view.
    case 'swept':
      return {
        ...state,
        step: 'run_closed',
        refusal: null,
        message: null,
        wire: append(state, {
          method: 'GET',
          path: `/auth/registrations/${state.runId}`,
          status: 409,
          response: {
            type: OUT_OF_STEP,
            title: OUT_OF_STEP_TITLE,
            step: state.step,
            status: 'abandoned'
          },
          provenance: 'shipped',
          note:
            'Seven days untouched and the sweep ends a run as abandoned — a week rather than a ' +
            'day, for the registrant who stops to find their number and comes back after the ' +
            'weekend. The run takes no more steps, so the read answers with where it stopped ' +
            'instead of a view. A cancel from another tab answers exactly the same, which is ' +
            'why the screen says both.'
        })
      };

    case 'sign-in':
      return signIn(state, action.email.trim(), action.password);

    case 'redeem-token':
      return {
        ...state,
        step: 'set_password',
        wire: append(state, {
          method: 'GET',
          path: '/password/reset#token=…',
          status: 200,
          provenance: 'shipped',
          note:
            'The redemption screen exists and takes the token from the URL fragment without ' +
            'stripping it, keeping the field editable for a code pasted by hand. What does ' +
            'not exist is anything that put the link in front of this person.'
        })
      };

    case 'set-password':
      return setPassword(state, action.password, action.currentPassword);

    case 'confirm-identifier':
      return confirmIdentifier(state, action.confirmed);

    // "None of these describes me", answered on the empty catalogue. It stays on the screen:
    // there is nowhere to send somebody who has just said the four registrations do not fit
    // them, and moving them to a different one would be the dead end pretending otherwise.
    case 'no-subject':
      return {
        ...state,
        message: NO_SUBJECT_MESSAGE,
        wire: append(state, {
          method: 'POST',
          path: '/my/entitlement-requests',
          request: { subject: 'none_of_these' },
          status: 202,
          provenance: 'not-built',
          note:
            'No route, no table, no queue. So this answer — the one that says a migrated ' +
            'account has arrived and belongs to none of the four rankings — is recorded ' +
            'nowhere, and the 10 586 stay invisible to staff exactly as long as that holds. ' +
            'It is the cheapest half of this screen to build and the one worth having first.'
        })
      };
  }
}

/**
 * The steps whose back is a plain move between screens the server never saw. A table rather
 * than four more switch arms, because the arms below are the ones that cost something and a
 * reader should be able to see at a glance which is which — and because a new client-side
 * screen that forgets to appear here has a Back button that does nothing, which is how the
 * recovery screens shipped broken the first time.
 */
const FREE_BACK: Partial<Record<StepCode, StepCode>> = {
  choose_subject: 'sign_in_entry',
  recover_request: 'sign_in_entry',
  recover_sent: 'sign_in_entry',
  recover_redeem: 'sign_in_entry'
};

/**
 * One step back — and what it costs, which is not the same on every screen.
 *
 * The shipped API has three backwards transitions, and the screen that offers each one sends
 * the request it names: `POST /identity {confirmed: false}` from the prompt and
 * `POST /revision` from the completion or a team's route step — both back to the anchor through
 * the one reset — and `POST /abandonment` from the anchor, which ends the run
 * (`back-to-chooser`, because the chooser belongs to another journey). Where the step is before
 * the run exists at all — the door and the chooser — going back is free, because there is
 * nothing on the server yet to go back through; inside an account the chooser's way back is to
 * the catalogue it came from. A team's last answer is taken back by asking without it
 * (`take-back`), which is no step back at all.
 */
function goBack(state: IRunState): IRunState {
  if (state.step === 'choose_subject' && state.signedIn) {
    return { ...state, step: 'nothing_offered', message: null };
  }
  const free = FREE_BACK[state.step];
  if (free) {
    return { ...state, step: free, message: null, passwordFailures: [] };
  }

  switch (state.step) {
    // "Enter a different number" — the revision survey-api added so that a registrant who
    // notices a typo at the last screen need not strand the run and start over, and which a
    // team's route step offers as well.
    case 'route':
    case 'complete': {
      const next = backToAnchor(state);
      return {
        ...next,
        wire: append(state, {
          method: 'POST',
          path: `/auth/registrations/${state.runId}/revision`,
          bearer: state.signedIn,
          status: 200,
          response: registrationView(next),
          provenance: 'shipped',
          note:
            'No body: “I typed the wrong number” is the whole request, and the right number ' +
            'arrives at the anchor this returns to. Everything the last number established ' +
            'goes — the lookup, both names, a redirect it earned — and the attempts stay: the ' +
            'reset “that is not me” makes, because the two are one correction reached from ' +
            'two screens.'
        })
      };
    }

    default:
      return state;
  }
}

/* --- registration ------------------------------------------------------- */

function submitAnchor(state: IRunState, typed: string, idType: string): IRunState {
  const survey = REGISTRATION[state.journey];
  const path = `/auth/registrations/${state.runId}/anchor`;
  const request = { value: typed, idType };

  // Refused where it was typed, before either question is asked: nothing is looked up, the
  // run does not move and the attempt is not spent — a typo must not use up the retry a real
  // miss is given (`RegistrationFlowService.SubmitAnchorAsync`).
  const value = idType === 'SEC_NUMBER' ? canonicalSecNumber(typed) : typed;
  if (value === null) {
    // Only the field changes: the product's refusal reaches the step's own state and nothing
    // else, so a retry message or an out-of-step notice already on the screen stays there.
    return {
      ...state,
      anchorFailure: SEC_NUMBER_REFUSAL,
      wire: append(state, {
        method: 'POST',
        path,
        request,
        status: 400,
        response: { errors: { value: [SEC_NUMBER_REFUSAL] } },
        provenance: 'shipped',
        note:
          'The one number whose shape the server judges, because the SEC register is matched ' +
          'exactly: 801- or 802-, at most six digits. Bare digits say neither prefix and may be ' +
          'a firm CRD typed into the wrong box. What passes is stored canonically, so neither ' +
          'the dash nor the absence of leading zeros is asked of the registrant.'
      })
    };
  }

  const attempts = state.attempts + 1;
  const held = heldIdentifier(heldType(state.journey, idType), value);
  const { result, row } = lookupRegistry(survey, idType, value, state.providerMode);
  const teams = state.journey === 'teams';
  // Only an `ok` names anybody — `RegistrationFlowService.Next`'s own rule, so no stray name on
  // a miss can reach the prompt.
  const named = result === 'ok' ? row : undefined;

  // What every answer carries forward. `refusal: null` because a step that advances clears the
  // last one — otherwise the out-of-step notice a stale tab produced would sit above every
  // screen after it.
  const base: IRunState = {
    ...state,
    anchorIdType: idType,
    anchorValue: value,
    attempts,
    lookup: result,
    registryName: named?.displayName ?? null,
    firmName: named?.firms?.[0]?.firmName ?? null,
    leadFirms: named?.firms ?? [],
    pairedIdentifier: named?.alsoKnownAs ?? null,
    displayName: null,
    linkTo: null,
    message: null,
    refusal: null,
    anchorFailure: null
  };
  /** The anchor's 200: the view of wherever the run went, which is what a client renders. */
  const answered = (next: IRunState, provenance: Provenance, note: string): IRunState => ({
    ...next,
    wire: append(state, {
      method: 'POST',
      path,
      bearer: state.signedIn,
      request,
      status: 200,
      response: registrationView(next),
      provenance,
      note
    })
  });

  // Our own data first, because it is the one answer that can end the run. The registry is
  // asked in the same request — it has to be, `external_fact.participant_id` being NOT NULL
  // while no participant exists yet — but a number an account already holds is refused
  // whatever the registry says about it.
  if (held?.hasAccount) {
    return {
      ...base,
      step: 'refused',
      refusal: {
        reason: 'identifier_unavailable',
        staffDetail:
          `The number is held by ${held.displayName}, who has an account. The run is ` +
          'abandoned: the anchor is the one thing this registrant cannot change, so a retry ' +
          'would be the same refusal.',
        runContinues: false
      },
      wire: append(state, {
        method: 'POST',
        path,
        bearer: state.signedIn,
        request,
        status: 409,
        response: {
          type: REFUSED,
          title: REFUSED_TITLE,
          status: 409,
          reason: 'identifier_unavailable'
        },
        provenance: 'shipped',
        note:
          'The body names the VALUE, never who holds it — otherwise the registration form ' +
          'becomes a directory of who is in the database (roadmap risk 4). Who holds it is ' +
          'in workflow_run_event, which the staff queue reads.' +
          (teams
            ? ' For a team, "held" means another team holds this lead CRD: a person’s own ' +
              'CRD of the same value refuses nothing.'
            : '')
      })
    };
  }

  // A number we already hold, with nobody able to sign in as it: the prompt, whatever the
  // registry said — which makes it the one way an `error` reaches the prompt at all.
  if (held) {
    return answered(
      { ...base, step: 'confirm_identity', linkTo: held, displayName: held.displayName },
      'shipped',
      (teams
        ? 'A team on file nobody can sign in for, held under this lead CRD. The prompt names ' +
          'the team beside the lead FINRA names, and the completion links the new account to ' +
          'it — and asks no route: the team keeps the routing it has. '
        : 'One of the 258 participants nothing can log in as. The prompt names them — the ' +
          'one branch where naming somebody from our own records is the point — and the ' +
          'completion links the new account to that participant rather than duplicating an ' +
          'advisor the rankings know. ') +
        'registryName rides beside displayName when the registry named somebody too, and the ' +
        'two need not agree.'
    );
  }

  if (result === 'ok') {
    return answered(
      { ...base, step: 'confirm_identity' },
      'shipped',
      teams
        ? 'BrokerCheck or IAPD matched and named the lead advisor. The firm it lists them at is ' +
          'not on this view: survey-api reads it against the wirehouse list once the lead is ' +
          'confirmed, and the next step says what it found. A yes stamps the lead CRD verified ' +
          'at completion, as for any registration. sightings is [] here: the study holds no CRD ' +
          'another team names.'
        : `${answerer(survey)} matched and named the number’s holder: registryName, the name ` +
          '“is this you?” asks about. A yes to it is the second half of a verification — the ' +
          'completion stamps verified_at, with verified_via = registry.'
    );
  }

  // `error` is about US: nothing was confirmed and nothing denied, so a new participant goes
  // on without a prompt — to the completion, where a notice says the number stays
  // unconfirmed (`ForNewParticipant`), or for a team to its route, asking every question. Only
  // the stubbed NIPR answers it here — the product's SEC ADV does too while its register is
  // empty or stale, which the study has no clock for, and BrokerCheck whenever FINRA cannot be
  // asked, which it has no switch for.
  if (result === 'error' && teams) {
    return answered(
      { ...base, step: 'route', ...restartRoute(UNASKED) },
      'shipped',
      'FINRA could not be asked, so nothing names a firm — and nothing names nobody either: ' +
        'no prompt, and the wirehouse list cannot be read, so the team is asked as one off it, ' +
        'from “submitting for an RIA?”. A wirehouse team reaches Wires through “Not ' +
        'Independent”. The lead CRD is stored unverified.'
    );
  }

  if (result === 'error') {
    return answered(
      { ...base, step: 'complete' },
      'stubbed',
      'A statement about US, never about the person: the flow carries on and the number is ' +
        'stored unverified. NIPR is still NotImplementedFactProvider, so every FSP ' +
        'registration takes this branch — as an FA one does whenever FINRA cannot be asked, ' +
        'and an RIA one whenever the SEC ADV register is empty or older than a week.'
    );
  }

  // not_found, at the RIA door: the notice at every attempt, and the run stays on the anchor —
  // another number, or the registrant's own choice to register as a team (`/redirection`).
  if (survey.noMatch) {
    return answered(
      { ...base, step: 'anchor' },
      'shipped',
      'notice is sec_not_registered with the sheet’s sentence, at every attempt (SR-416’s B5-12): ' +
        'no second-miss branch. The product draws the sentence as sent, the RIA-vs-Teams ' +
        'criteria (ours) under it, and “Register as a team instead”, which posts /redirection. ' +
        'SEC ADV matches registered advisers only, so an exempt reporting adviser’s 802- number ' +
        'answers it too.'
    );
  }

  // not_found. The first attempt offers a retry, because a typo is likelier than somebody
  // with no number at all; only the second branches.
  if (attempts < 2) {
    return answered(
      {
        ...base,
        step: 'anchor',
        // The live anchor's words (`missStatement`), and like them promising nothing about a
        // second miss: what it does differs by survey, and the view says which once it has.
        message:
          `${missStatement(survey.surveyCode)}. Check it and try once more — if it ` +
          'is right, submit it again.'
      },
      'shipped',
      'not_found is a statement about the PERSON, so it is treated as one. FINRA answers an ' +
        'unknown CRD with 200 and hits.total = 0 — one field in a body separates this from ' +
        '“we could not ask” (roadmap risk 3). SEC ADV matches registered advisers only, so an ' +
        'exempt reporting adviser’s 802- number answers it too.'
    );
  }

  // A lead FINRA does not know names no firm: the tree opens at trust company or RIA.
  if (teams) {
    return answered(
      { ...base, step: 'route', ...restartRoute(NO_FIRM) },
      'shipped',
      'A team carries on without the number, as FA does — nothing is stored — and with no ' +
        'firm to route by, so the gating questions start at trust company or RIA.'
    );
  }

  return answered(
    { ...base, step: 'complete' },
    'shipped',
    'FA and FSP have nowhere to send an unmatched registrant, so the flow carries on and ' +
      '**no identifier is stored at all** — an unverified number nobody recognises is worse ' +
      'than none.'
  );
}

/**
 * "Register as a team instead" (`/redirection`, no body) — the RIA door's way out, on a run whose
 * last lookup answered `not_found`. The run is abandoned in the same request and nothing is made;
 * the way on is a new run, the team survey's own, by the lead advisor's CRD.
 */
function redirectRun(state: IRunState): IRunState {
  const survey = REGISTRATION[state.journey];
  if (!survey.noMatch || state.step !== 'anchor' || state.lookup !== 'not_found') return state;
  const next: IRunState = {
    ...state,
    step: 'redirected',
    redirected: true,
    message: null,
    destination: {
      surveyCode: survey.noMatch.toSurvey,
      subjectKind: 'org',
      why: 'The SEC lists no registered adviser under that number, and the registrant chose to ' +
        'register as a team instead.'
    }
  };
  return {
    ...next,
    wire: append(state, {
      method: 'POST',
      path: `/auth/registrations/${state.runId}/redirection`,
      bearer: state.signedIn,
      status: 200,
      response: registrationView(next),
      provenance: 'shipped',
      note:
        'destinationSurvey TEAMS, redirected true, and the run abandoned in this same request: ' +
        'no account, no firm. A GET of the run afterwards is 409 out-of-step with no destination, ' +
        'which is why the product keeps the survey a tab started. 409 on any other run — an ok ' +
        'or error lookup, another survey, a second call.'
    })
  };
}

/** The route step from its first question: what a route asks before any answer, with none
 *  given — where the step starts, a reload or a resumed tab included. */
function restartRoute(route: IRoute): Partial<IRunState> {
  const first = evaluateRoute(route, {});
  return {
    route,
    question: first.kind === 'ask' ? first.question : null,
    given: [],
    takenBack: null,
    answerRefused: null
  };
}

/**
 * "Our lead advisor has no CRD number" (`/no-anchor`) — TEAMS alone, since survey-api refuses it
 * for every other survey. Nothing is looked up and nothing will be stored, and with no firm to
 * route by the gating questions open at the trust company one (TEAM-RG-001).
 */
function declareNoAnchor(state: IRunState): IRunState {
  const next: IRunState = {
    ...state,
    step: 'route',
    ...restartRoute(NO_FIRM),
    lookup: null,
    message: null,
    refusal: null,
    anchorFailure: null
  };
  return {
    ...next,
    wire: append(state, {
      method: 'POST',
      path: `/auth/registrations/${state.runId}/no-anchor`,
      bearer: state.signedIn,
      status: 200,
      response: registrationView(next),
      provenance: 'shipped',
      note:
        'No body. TEAMS only — any other survey answers 400 on value, a person’s or a firm’s ' +
        'registration having nothing but its number to anchor on. lookup stays null, which is ' +
        'how a later screen tells “no CRD” from a CRD FINRA did not know.'
    })
  };
}

/**
 * The route step's answers — every one so far, each time (`/routing`). They stop at the next
 * question, reach an outcome that moves the run to the completion, or make the team an RIA firm,
 * which ends the run redirected with nothing created. A refused answer leaves the run where it
 * was, said on its question.
 */
function answerRoute(state: IRunState, given: IRouteAnswer[]): IRunState {
  const route = state.route ?? NO_FIRM;
  const answers = routingAnswers(given);
  const path = `/auth/registrations/${state.runId}/routing`;
  const request = { answers };
  const result = evaluateRoute(route, answers);

  if (result.kind === 'refused') {
    const message = describeRefusal(result.reason);
    return {
      ...state,
      answerRefused: [message],
      wire: append(state, {
        method: 'POST',
        path,
        bearer: state.signedIn,
        request,
        status: 400,
        response: { errors: { [`answers.${result.code}`]: [message] } },
        provenance: 'shipped',
        note:
          'Judged by survey-api alone: a choice is its option’s exact string, and an answer to ' +
          'a question these answers never reach is refused rather than dropped. Nothing moved, ' +
          'so nothing is re-read.'
      })
    };
  }

  const moved: IRunState = {
    ...state,
    given,
    takenBack: null,
    answerRefused: null,
    refusal: null
  };
  const answeredWith = (next: IRunState, note: string): IRunState => ({
    ...next,
    wire: append(state, {
      method: 'POST',
      path,
      bearer: state.signedIn,
      request,
      status: 200,
      response: registrationView(next),
      provenance: 'shipped',
      note
    })
  });

  if (result.kind === 'ask') {
    return answeredWith(
      { ...moved, step: 'route', question: result.question },
      'The next question, and nothing kept: survey-api stores no answer between calls, so ' +
        'this set is posted whole again with the next one added.'
    );
  }

  if (result.outcome === 'ria') {
    return answeredWith(
      {
        ...moved,
        step: 'redirected',
        question: null,
        redirected: true,
        destination: {
          surveyCode: 'RIA_FIRM',
          subjectKind: 'org',
          why: 'The answers make the team an RIA firm, which registers by its SEC number.'
        }
      },
      'The run is abandoned in this same request and nothing is created — no account, no ' +
        'team. The way on is a new run, the RIA firm survey’s own registration, by the firm’s ' +
        'SEC number: nothing typed here was the firm’s.'
    );
  }

  const routing = teamRouting(result.outcome, result.firm, answers);
  return answeredWith(
    { ...moved, step: 'complete', question: null, routing, destination: teamDestination(routing) },
    'An outcome, kept on the run until the completion writes it as the team’s own routing ' +
      '(source registration). The view does not say which path: the catalogue’s teamRouting ' +
      'does, once the team exists.'
  );
}

function decideIdentity(state: IRunState, confirmed: boolean): IRunState {
  const request = { confirmed };
  // See `submitAnchor`: advancing a step clears whatever the last one was refused for.
  state = { ...state, refusal: null };

  if (!confirmed) {
    const next = backToAnchor(state);
    return {
      ...next,
      wire: append(state, {
        method: 'POST',
        path: `/auth/registrations/${state.runId}/identity`,
        bearer: state.signedIn,
        request,
        status: 200,
        response: registrationView(next),
        provenance: 'shipped',
        note:
          '“That is not me” is neither an error nor a not-found: the number was right for ' +
          'somebody, just not for the person typing it. Everything the previous answer ' +
          'established is dropped and the attempt count is kept, because the retry-then-branch ' +
          'rule reads it.'
      })
    };
  }

  // A new team's route is read here, once its lead is confirmed; a team on file keeps the
  // routing it has and takes no route at all. Every entry of SR-416's tree asks a question
  // first, so a new team always goes to the route step.
  if (state.journey === 'teams' && !state.linkTo) {
    const next: IRunState = { ...state, step: 'route', ...restartRoute(routeOf(state.leadFirms)) };

    return {
      ...next,
      wire: append(state, {
        method: 'POST',
        path: `/auth/registrations/${state.runId}/identity`,
        bearer: state.signedIn,
        request,
        status: 200,
        response: registrationView(next),
        provenance: 'shipped',
        note:
          'The route, read here: FINRA’s current firms for the lead, against the wirehouse ' +
          'list (SR-416’s B5-1). A firm on it asks the private-wealth question; no firm at all ' +
          'the trust company one; any other firm “submitting for an RIA?”. firmName is the ' +
          'register’s label where one register firm maps the lead’s firms, else FINRA’s name. ' +
          'A FINRA firm the register does not hold is recorded for staff, under Team firms’ ' +
          '“Seen at registration”.'
      })
    };
  }

  const next: IRunState = { ...state, step: 'complete' };
  return {
    ...next,
    wire: append(state, {
      method: 'POST',
      path: `/auth/registrations/${state.runId}/identity`,
      bearer: state.signedIn,
      request,
      status: 200,
      response: registrationView(next),
      provenance: 'shipped',
      note:
        'The view carries the field names the completion body may use. A name the survey ' +
        'does not declare is ignored rather than refused — the client renders from `fields`, ' +
        'and a stale one must not be able to stop somebody registering.'
    })
  };
}

function complete(
  state: IRunState,
  email: string,
  password: string,
  fields: Record<string, string>
): IRunState {
  const path = `/auth/registrations/${state.runId}/completion`;
  // Signed in, the body is the fields alone: survey-api makes no account on a bound run, and
  // mails nothing, so there is no address, password or policy to judge.
  const request = state.signedIn ? { fields } : { email, password: '••••••••', fields };

  if (!state.signedIn) {
    const refused = refuseCredentials(state, email, password, path, request);
    if (refused) return refused;
  }

  const survey = REGISTRATION[state.journey];
  const teams = state.journey === 'teams';
  const numberless = (state.lookup === 'not_found' || state.lookup === null) && !state.linkTo;
  const stored: Nullable<IStoredIdentifier> = numberless
    ? null
    : {
        idType: heldType(state.journey, state.anchorIdType),
        value: state.anchorValue ?? '',
        // `CorroboratedByRegistry`: the registry named the holder AND the registrant said yes
        // to that name — never their word alone, and never an `ok` that named nobody.
        verified: state.lookup === 'ok' && state.registryName !== null,
        // Left null by registration, so the catalogue asks again (the plan's ask 1).
        selfConfirmation: null,
        holder: state.linkTo?.displayName ?? composedName(survey, fields)
      };

  // A redirected run and a team run both arrive here carrying a destination their own answers
  // chose, and neither may be overwritten with the survey the run was opened for: the RIA
  // redirect lands on TEAMS, and a team's path is its route's. A team on file keeps the routing
  // it has, which this run never read.
  const destination: IDestination =
    state.redirected || state.routing
      ? state.destination
      : {
          surveyCode: survey.surveyCode,
          subjectKind: survey.subjectKind,
          why: whyThisSurvey(state)
        };

  // `EnqueueLookup`: nothing where no number is kept, there being no identifier to record a
  // fact about.
  const lookupJob: IWireCall[] = numberless
    ? []
    : [
        {
          method: 'POST',
          path: '(job) external_fact_lookup',
          request: { participantId: '…', factKey: survey.factKey, value: state.anchorValue },
          status: 202,
          provenance: survey.providerStubbed ? 'stubbed' : 'shipped',
          note: survey.providerStubbed
            ? 'The durable lookup is enqueued inside the completion’s transaction, once ' +
              'there is a participant to record an answer about. NotImplementedFactProvider ' +
              'writes one external_fact with status = error and a reason — never ' +
              'not_found, which would be a statement about the person.'
            : 'The durable lookup is enqueued inside the completion’s transaction, once ' +
              `there is a participant to record an answer about: the second call to ` +
              `${survey.registry} for this number, and the one that writes the ` +
              'external_fact a later gate reads, with the queue’s retries behind it.' +
              (teams ? ' A lead CRD is asked as a person’s CRD, and its fact tied to it by value.' : '')
        }
      ];

  const created: IWireCall = {
    method: 'POST',
    path,
    bearer: state.signedIn,
    request,
    status: 201,
    response: {
      runId: state.runId,
      accountId: '…',
      participantId: state.linkTo ? '(the existing participant)' : '…',
      destinationSurvey: destination.surveyCode,
      redirected: state.redirected
    },
    provenance: 'shipped',
    note:
      (state.signedIn
        ? 'Into the caller’s account: a firm or a team is a new or claimed org participant ' +
          'linked to it, and a person’s number joins the account’s one person — or makes a ' +
          'new person where the account acts for none or several. '
        : 'One transaction: the account, the participant, the account_participant link and ' +
          'the identifier. ') +
      (state.routing ? 'A new team’s route outcome is written as its routing decision. ' : '') +
      '**No response**, for any survey: the respondent starts it from the catalogue, and the ' +
      'first response proposes what registration collected (source registration). The number ' +
      'is re-checked first, because somebody may have finished registering it since the anchor ' +
      'step, and UNIQUE(id_type, value) catches the race inside the transaction.'
  };

  const identifiers: IWireCall[] = stored
    ? [
        {
          method: 'GET',
          path: '/api/v1/my/identifiers',
          status: 200,
          response: [identifierRow(stored, null)],
          provenance: 'shipped',
          note:
            'The catalogue reads the numbers held for this account and asks about each one ' +
            'nobody has answered. Registration leaves self_confirmation null — whatever was ' +
            'said at confirm_identity — so the card asks this registrant again, as the owner ' +
            'ruled it should until survey-api sets it (the registration-flow plan’s ask 1).'
        }
      ]
    : [];

  const signIn: IWireCall[] = state.signedIn
    ? []
    : [
        {
          method: 'POST',
          path: '(job) auth_mail',
          request: { kind: 'email_verification', accountId: '…' },
          status: 202,
          provenance: 'shipped',
          note:
            'Queued in the same transaction (block 6): a link to /email/verify that confirms ' +
            'the address the registrant typed. It gates nothing — the landing only asks — and ' +
            'on a test environment it goes to the test inbox the completion named, or is ' +
            'withheld when none was, rather than the registration being refused for it.'
        },
        {
          method: 'POST',
          path: '/auth/login',
          request: { email, password: '••••••••' },
          status: 200,
          response: { token: '…', user: { email, mustChangePassword: false } },
          provenance: 'shipped',
          note:
            'The completion issues no session, so the page signs in with what was just typed ' +
            'and lands on the catalogue. A sign-in refused here — a throttle, a race — goes to ' +
            '/login saying the account exists, never back into a run that has finished.'
        }
      ];

  return {
    ...state,
    email: state.signedIn ? state.email : email,
    passwordFailures: [],
    storedIdentifier: stored,
    destination,
    // No completion is redirected since SR-416: both redirects end their run with nothing made.
    step: 'landed',
    refusal: null,
    added: state.signedIn
      ? {
          survey: survey.surveyCode,
          destinationSurvey: destination.surveyCode,
          redirected: state.redirected
        }
      : null,
    wire: append(
      state,
      created,
      ...lookupJob,
      ...signIn,
      ...identifiers,
      catalogueRead(state, destination)
    )
  };
}

/**
 * The completion's two refusals of what an anonymous registrant typed: the password policy's
 * rules, and an address `account.email` already holds. Null where neither applies.
 */
function refuseCredentials(
  state: IRunState,
  email: string,
  password: string,
  path: string,
  request: Record<string, unknown>
): Nullable<IRunState> {
  const failures = passwordFailures(password, email);
  if (failures.length > 0) {
    return {
      ...state,
      email,
      passwordFailures: failures,
      message: null,
      refusal: null,
      wire: append(state, {
        method: 'POST',
        path,
        request,
        status: 400,
        response: { errors: { password: failures } },
        provenance: 'shipped',
        note: 'The rule that failed, never the password that failed it.'
      })
    };
  }

  // Both collisions answer the registrant alike — `address_unavailable`, the run left in
  // progress — so both stay on this screen with the same line on the address field. What
  // tells them apart is the run's event, which staff read and the registrant never does.
  const collision: AddressCollision | undefined = TAKEN_ADDRESSES[email.toLowerCase()];
  if (!collision) return null;
  return {
    ...state,
    email,
    passwordFailures: [],
    refusal: {
      reason: 'address_unavailable',
      staffDetail:
        collision === 'duplicate_address'
          ? 'duplicate_address — somebody registering twice. Recorded outside the ' +
            'transaction so it survives the rollback, and the run is left in progress.'
          : 'address_belongs_to_another_account — one of the 54 whose participant exists ' +
            'and whose address is already some other account’s, made by legacy having no ' +
            'unique index on its login addresses. Their way out is staff-side: a link on ' +
            'their participant’s card to the account they should use (roadmap F3, block 6).',
      runContinues: true,
      deadEnd: collision === 'address_belongs_to_another_account'
    },
    wire: append(state, {
      method: 'POST',
      path,
      request,
      status: 409,
      response: {
        type: REFUSED,
        title: REFUSED_TITLE,
        status: 409,
        reason: 'address_unavailable'
      },
      provenance: 'shipped',
      note:
        'The caller learns one thing — address_unavailable — and the event tells the two ' +
        'apart, because they are different problems: a duplicate is somebody registering ' +
        'twice, and one of the 54 holds an address legacy let another account claim, whose ' +
        'way out is staff-side — a link on the participant card (roadmap F3, block 6).'
    })
  };
}

/**
 * `GET /my/cycles` as the landing reads it: the survey the registration was for, offered to
 * start with no response in it. A team's entry carries its routing, which is what the start
 * card starts it by.
 */
function catalogueRead(state: IRunState, destination: IDestination): IWireCall {
  const routing = state.routing;
  return {
    method: 'GET',
    path: '/api/v1/my/cycles',
    status: 200,
    response: [
      {
        survey: { code: destination.surveyCode },
        cycle: { code: '2026' },
        phase: 'active',
        eligibleParticipants: [
          {
            participantId: '…',
            displayName: state.storedIdentifier?.holder ?? state.linkTo?.displayName ?? '…',
            kind: destination.subjectKind,
            teamRouting:
              state.journey !== 'teams'
                ? null
                : routing
                  ? {
                      outcome: routing.outcome,
                      pathCode: routing.pathCode,
                      source: routing.source,
                      firm: routing.firm && {
                        code: routing.firm.code,
                        label: routing.firm.label
                      },
                      answers: routing.answers,
                      decidedAt: '…'
                    }
                  : '(the team’s routing, as it stood)'
          }
        ],
        responses: []
      }
    ],
    provenance: 'shipped',
    note:
      'Offered because the registration completed: a completed registration, not redirected, ' +
      'entitles its participant to its survey. responses is empty — the start button is ' +
      'POST /api/v1/responses, and ' +
      (state.journey === 'teams'
        ? 'for a routed team it sends no pathCode: survey-api fills the routed one.'
        : 'that create proposes what registration collected into the survey.')
  };
}

/** `CompleteRegistrationService.DisplayName`, mirrored: the display-name fields in catalogue
 *  order, blanks dropped, joined by a space — null when none was given. */
function composedName(survey: IRegistrationSurvey, fields: Record<string, string>) {
  const name = survey.fields
    .filter((field) => field.partOfDisplayName)
    .map((field) => fields[field.name]?.trim())
    .filter(Boolean)
    .join(' ');
  return name.length > 0 ? name : null;
}

function whyThisSurvey(state: IRunState): string {
  switch (state.journey) {
    case 'fa':
      return 'Registered as an individual advisor, so the catalogue offers the FA survey and ' +
        'nothing else.';
    case 'fsp':
      return 'Registered on an NPN, so the catalogue offers the FSP survey. A CRD entered ' +
        'later branches the survey; it does not add one.';
    case 'ria':
      return 'The participant is the firm, so the catalogue offers the RIA firm survey.';
    case 'teams':
      return 'A team on file, now linked: it keeps the routing it had, and the catalogue ' +
        'starts it by that.';
    default:
      return 'Where this registration started.';
  }
}

/* --- migrated accounts --------------------------------------------------- */

/**
 * Whose credentials the sign-in screen accepts, which is a property of the journey rather
 * than of the screen: the front door and the returning advisor share one account, and the
 * empty-catalogue journey has its own — the whole point of it being what that account does
 * *not* hold.
 */
export function signInAccount(journey: JourneyCode): { email: string; password?: string } {
  return journey === 'unentitled' ? UNENTITLED_ACCOUNT : MIGRATED_ACCOUNTS.returning;
}

/** Whose number the identifier confirmation asks about: the activation journey's account, or
 *  the one every other sign-in lands as. */
export function migratedAccount(journey: JourneyCode): IMigratedAccount {
  return journey === 'activation' ? MIGRATED_ACCOUNTS.activation : MIGRATED_ACCOUNTS.returning;
}

/**
 * A row of `GET /my/identifiers`, as `MyIdentifierDto` spells it. Nothing has corroborated a
 * migrated number, so both verification members are null there; a registration's carries the
 * registry's stamp where it earned one. The owner's own answer is the third member, and the
 * only one their answer writes.
 */
function identifierRow(
  identifier: Pick<IStoredIdentifier, 'idType' | 'value' | 'verified'>,
  selfConfirmation: Nullable<SelfConfirmation>
): Record<string, unknown> {
  return {
    identifierId: '…',
    participantId: '…',
    idType: identifier.idType,
    value: identifier.value,
    verifiedAt: identifier.verified ? '…' : null,
    verifiedVia: identifier.verified ? 'registry' : null,
    selfConfirmation
  };
}

/** A migrated account's one number, which nothing has corroborated. */
function migratedIdentifier(account: IMigratedAccount): IStoredIdentifier {
  return {
    ...account.identifier,
    verified: false,
    selfConfirmation: null,
    holder: account.displayName
  };
}

function signIn(state: IRunState, email: string, password: string): IRunState {
  const account = signInAccount(state.journey);
  const ok = email.toLowerCase() === account.email && password === account.password;

  if (!ok) {
    return {
      ...state,
      message: 'Those credentials do not match an account.',
      wire: append(state, {
        method: 'POST',
        path: '/auth/login',
        request: { email, password: '••••••••' },
        status: 401,
        provenance: 'shipped',
        note: 'Empty body, like every other refusal on the auth surface.'
      })
    };
  }

  return {
    ...state,
    email,
    message: null,
    step: 'forced_change',
    wire: append(state, {
      method: 'POST',
      path: '/auth/login',
      request: { email, password: '••••••••' },
      status: 200,
      response: { token: '…', user: { email, mustChangePassword: true } },
      provenance: 'shipped',
      note:
        'must_change_password is true on all 55 054 migrated accounts and ' +
        'password_changed_at is null on every one — no migrated account has completed a ' +
        'first login yet. survey-api answers 403 on every endpoint but /auth/me until it clears.'
    })
  };
}

function setPassword(state: IRunState, password: string, currentPassword?: string): IRunState {
  const viaToken = state.journey === 'activation';
  const account = viaToken ? MIGRATED_ACCOUNTS.activation : signInAccount(state.journey);

  // `ChangePasswordService`'s order: the current password first, answered as an empty-bodied
  // 401, and the policy only once it has passed.
  if (!viaToken && currentPassword !== account.password) {
    return {
      ...state,
      passwordFailures: [],
      message: 'That is not your current password.',
      wire: append(state, {
        method: 'POST',
        path: '/auth/password/change',
        request: { currentPassword: '••••••••', newPassword: '••••••••' },
        status: 401,
        provenance: 'shipped',
        note: 'Empty body, like every other refusal on the auth surface.'
      })
    };
  }

  const failures = passwordFailures(password, account.email);
  if (failures.length > 0) {
    return { ...state, message: null, passwordFailures: failures };
  }

  // The one journey where the change clears the 403 and reveals nothing behind it. The
  // catalogue read is logged because it is the evidence: an empty array is an answer, and
  // the screen it produces has to be told apart from a request that failed.
  const empty = state.journey === 'unentitled';

  const calls: IWireCall[] = [
    {
      method: 'POST',
      path: viaToken ? '/auth/password/reset' : '/auth/password/change',
      request: viaToken
        ? { token: '…', newPassword: '••••••••' }
        : { currentPassword: '••••••••', newPassword: '••••••••' },
      status: 204,
      provenance: 'shipped',
      note: viaToken
        ? 'The redemption route exists and answers 204 with **no session**, so it ends on the ' +
          'sign-in screen. What does not exist is the campaign that would mail this token: ' +
          'survey-api mails a reset somebody asked for and a seat an admin created, and ' +
          'nothing in bulk. Roadmap B3.'
        : 'The forced change is shipped, and so is what follows it.'
    }
  ];

  if (empty) {
    calls.push({
      method: 'GET',
      path: '/api/v1/my/cycles',
      status: 200,
      response: [],
      provenance: 'shipped',
      note:
        'Scoped since SR-213 block 1: a cycle is listed for a participant that has ' +
        'answered that survey before or holds a number it anchors on. This ' +
        'participant is neither, and 10 586 of the 55 056 accounts are in the same ' +
        'position — measured on the migrated target, where 13 971 participants hold ' +
        'only a LEGACY_USER_ID and an address. The array is correct and the screen ' +
        'after it is the problem.'
    });
  } else {
    calls.push({
      method: 'GET',
      path: '/api/v1/my/identifiers',
      status: 200,
      response: [identifierRow(migratedIdentifier(migratedAccount(state.journey)), null)],
      provenance: 'shipped',
      note:
        'Every number held about the participants this account acts for, and what anyone has ' +
        'said about each: verifiedAt and verifiedVia for a corroboration, selfConfirmation ' +
        'for the owner’s own answer. A null selfConfirmation is “never asked”, and it is what ' +
        'decides whether to ask — a row is never left out for being unanswered. The name on ' +
        'the card joins on participantId to GET /my/participants.'
    });
  }

  return {
    ...state,
    email: account.email,
    message: null,
    passwordFailures: [],
    step: empty ? 'nothing_offered' : 'confirm_identifier',
    wire: append(state, ...calls)
  };
}

/**
 * The owner's answer, from either place the study asks it: the migrated journeys' screen, which
 * then lands, or the card on a registration's landing, which stays where it is — the product's
 * catalogue, where the card is one among the rest and answering it goes nowhere.
 */
function confirmIdentifier(state: IRunState, confirmed: boolean): IRunState {
  const selfConfirmation: SelfConfirmation = confirmed ? 'confirmed' : 'denied';
  if (state.step === 'landed') return answerOnLanding(state, selfConfirmation);

  const account = migratedAccount(state.journey);

  return {
    ...state,
    step: 'landed',
    // Written to self_confirmation and nowhere else: verified_at means corroborated by
    // something other than the owner's word, and a click is the owner's word.
    storedIdentifier: { ...migratedIdentifier(account), selfConfirmation },
    destination: {
      surveyCode: account.surveyCode,
      subjectKind: 'person',
      why: confirmed
        ? 'A migrated advisor participant, so the catalogue offers the survey their migrated ' +
          'responses sit in — as it did before the answer: a confirmation gates nothing.'
        : 'The respondent says the number on file is not theirs. Nothing is cleared and ' +
          'nothing is blocked: the number stays as it was, and so does the survey.'
    },
    wire: append(state, {
      method: 'POST',
      path: '/api/v1/my/identifier-confirmations',
      request: { identifierId: '…', selfConfirmation },
      status: 200,
      response: identifierRow(migratedIdentifier(account), selfConfirmation),
      provenance: 'shipped',
      note: confirmed
        ? 'Written to self_confirmation with an identifier_self_confirmed audit event, and ' +
          'verified_at stays null. survey-api ruled a respondent’s own word no corroboration ' +
          '(its Q4): that column is what the staff queues read to find who still needs a ' +
          'look, and a self-click stamping it would empty them of exactly the migrated rows ' +
          'worth working. It only ever sets, so a second answer is answered with the first.'
        : 'Clears nothing — not the number, not a verification, not the responses the ' +
          'rankings were built from — and blocks nothing. It writes self_confirmation = ' +
          'denied and an identifier_self_denied audit event, which is the one staff filter ' +
          'for. It only ever sets, so the repair for a denial made in error is not a second ' +
          'answer.'
    })
  };
}

/**
 * The card on a registration's landing, answered. Nothing moves: the row takes the answer, and
 * `verified_at` stays what the registration made it — stamped only where the registry named
 * the holder and the registrant agreed, which is a different fact from this one.
 */
function answerOnLanding(state: IRunState, selfConfirmation: SelfConfirmation): IRunState {
  if (!state.storedIdentifier) return state;
  const storedIdentifier = { ...state.storedIdentifier, selfConfirmation };

  return {
    ...state,
    storedIdentifier,
    wire: append(state, {
      method: 'POST',
      path: '/api/v1/my/identifier-confirmations',
      request: { identifierId: '…', selfConfirmation },
      status: 200,
      response: identifierRow(storedIdentifier, selfConfirmation),
      provenance: 'shipped',
      note:
        selfConfirmation === 'confirmed'
          ? 'The same question the registrant answered at confirm_identity, asked again ' +
            'because registration did not record that answer (the plan’s ask 1). It writes ' +
            'self_confirmation and an audit event, and leaves verified_at as the registration ' +
            'set it: the owner’s word corroborates nothing (survey-api’s Q4).'
          : 'Clears nothing — not the number, not the registry’s stamp where the ' +
            'registration earned one, not the survey the catalogue offers — and blocks ' +
            'nothing. It writes self_confirmation = denied and an identifier_self_denied ' +
            'audit event, which is what staff filter for.'
    })
  };
}
